import React from 'react';

// ── Shared building blocks ──────────────────────────────────────────────────

const S = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={`skeleton ${props.className ?? ''}`} />
);

// ── Named skeleton variants matching each page's real layout ───────────────

/** Dashboard: 4 stat cards + activity list */
export const DashboardSkeleton: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
    {/* Header */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <S className="sk-title" style={{ width: 220 }} />
      <S className="sk-text"  style={{ width: 300 }} />
    </div>

    {/* 4 stat cards */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <S key={i} className="sk-card" style={{ height: 120, padding: 20 }} />
      ))}
    </div>

    {/* Welcome widget + quick actions side by side */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <S className="sk-card" style={{ height: 140 }} />
      <S className="sk-card" style={{ height: 140 }} />
    </div>

    {/* Recent activity list */}
    <S className="sk-card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <S className="sk-title" style={{ width: 160 }} />
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="sk-row">
          <S className="sk-circle" style={{ width: 32, height: 32 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <S className="sk-text" style={{ width: '70%' }} />
            <S className="sk-text" style={{ width: '40%', opacity: 0.6 }} />
          </div>
        </div>
      ))}
    </S>
  </div>
);

/** PRDWriter / Docs list: header + cards grid */
export const DocListSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <S className="sk-title" style={{ width: 180 }} />
        <S className="sk-text"  style={{ width: 260 }} />
      </div>
      <S style={{ width: 120, height: 38, borderRadius: 8 }} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
      {Array.from({ length: count }).map((_, i) => (
        <S key={i} className="sk-card" style={{ height: 160 }} />
      ))}
    </div>
  </div>
);

/** Users page: member list rows */
export const UserListSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid #F0E8E0', borderRadius: 16, overflow: 'hidden', background: '#fff' }}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="sk-row" style={{ padding: '16px 20px', borderTop: i > 0 ? '1px solid #F5EEE8' : 'none' }}>
        <S className="sk-circle" style={{ width: 38, height: 38 }} />
        <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <S className="sk-text" style={{ width: '55%' }} />
          <S className="sk-text" style={{ width: '35%', opacity: 0.6 }} />
        </div>
        <S className="sk-text" style={{ flex: 1, height: 12 }} />
        <S style={{ width: 60, height: 22, borderRadius: 12, flex: 'none' }} />
        <S style={{ width: 64, height: 30, borderRadius: 8, flex: 'none' }} />
      </div>
    ))}
  </div>
);

/** Meeting Plan list */
export const MeetingListSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    {Array.from({ length: count }).map((_, i) => (
      <S key={i} className="sk-card" style={{ height: 80, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, background: 'white' }}>
        <S className="sk-circle" style={{ width: 40, height: 40, flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <S className="sk-text" style={{ width: '50%' }} />
          <S className="sk-text" style={{ width: '30%', opacity: 0.6 }} />
        </div>
        <S style={{ width: 80, height: 28, borderRadius: 8 }} />
      </S>
    ))}
  </div>
);

/** Whiteboard list: grid of cards */
export const WhiteboardListSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
    {Array.from({ length: count }).map((_, i) => (
      <S key={i} className="sk-card" style={{ height: 180 }} />
    ))}
  </div>
);

/** Generic single-column list rows */
export const ListRowsSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="sk-row" style={{ padding: '10px 0' }}>
        <S className="sk-circle" style={{ width: 32, height: 32 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <S className="sk-text" style={{ width: `${55 + (i % 3) * 12}%` }} />
          <S className="sk-text" style={{ width: '35%', opacity: 0.6 }} />
        </div>
      </div>
    ))}
  </div>
);

/** TaskBoard list: left panel rows */
export const TaskBoardRowsSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="sk-row" style={{ padding: '14px 16px', borderBottom: '1px solid #F5EEE8' }}>
        <S style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <S className="sk-text" style={{ width: `${50 + (i % 3) * 15}%` }} />
          <S className="sk-text" style={{ width: '30%', opacity: 0.6 }} />
        </div>
        <S style={{ width: 16, height: 16, borderRadius: 3, flexShrink: 0 }} />
      </div>
    ))}
  </div>
);
